
// EarningCard.js
import PropTypes from 'prop-types';
import React from 'react';
import { useState,useEffect } from 'react';


import { useTheme, styled } from '@mui/material/styles';
import {
  Avatar,
  Box,
  Grid,
  MenuItem,
  Typography,
  Fade ,
  Select ,
  OutlinedInput,
  // Select
} from '@mui/material';
import { FormControl } from '@mui/material';
import {stockData , getAllDevices, getRequestReport, getStockDetails } from 'API/Stock';


// project imports
import MainCard from 'ui-component/cards/MainCard';
import SkeletonEarningCard from 'ui-component/cards/Skeleton/EarningCard';
import ApplicationSalesCard from './ApplicationSalesCard';
import Divider from '@mui/material/Divider';
// assets
import EarningIcon from 'assets/images/icons/earning.svg';


// styled wrapper for second card
const CardWrapper = styled(MainCard)(({ theme }) => ({
  height: '100%',
  backgroundColor: theme.palette.primary.dark,
  color: '#fff',
  overflow: 'hidden',
  position: 'relative', 
  '&>div': {
    position: 'relative',
    zIndex: 5
  },
  '&:after': {
    content: '""',
    position: 'absolute',
    width: 210,
    height: 210,
    background: theme.palette.primary[800],
    borderRadius: '50%',
    zIndex: 1,
    top: -85,
    right: -95,
    [theme.breakpoints.down('sm')]: {
      top: -105,
      right: -140
    }
  },
  '&:before': {
    content: '""',
    position: 'absolute',
    zIndex: 1,
    width: 210,
    height: 210,
    background: theme.palette.primary[800],
    borderRadius: '50%',
    top: -125,
    right: -15,
    opacity: 0.5,
    [theme.breakpoints.down('sm')]: {
      top: -155,
      right: -70
    }
  }
}));

const EarningCard = ({ isLoading  }) => {
  const theme = useTheme();
  const [stockInfo, setStockInfo] = useState(null);
  const [selectedOption, setSelectedOption] = useState('');
  const [deviceList, setDeviceList] = useState([]);
  const [deviceMap, setDeviceMap] = useState({});
  // const [ setSelectedProductIds] = useState([]);
  const [loadingStock, setLoadingStock] = useState(false);

  const stockIn = stockInfo?.stocks_in ?? 0;
  const stockOut = stockInfo?.stocks_out ?? 0;
  const totalStock = stockInfo?.total_stocks ?? stockIn + stockOut;

  const [requestReport, setRequestReport] = useState({
    purchase_request_pending: 0,
    purchase_order_pending: 0,
    payment_order_pending: 0
  });



  const [stockDetails, setStockDetails] = useState({
    to_be_shipped: 0,
    out_of_delivery: 0,
    orders_delivered: 0
  });



  useEffect(() => {
    const fetchDeviceListAndDefaultStock = async () => {
      // Fetch device list
      const data = await getAllDevices();
      if (data?.devices && typeof data.devices === 'object') {
        setDeviceMap(data.devices);
        setDeviceList(Object.keys(data.devices));
      }

      // Fetch overall stock (no product_ids)
      const today = new Date();
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(today.getMonth() - 1);
      const formatDate = (d) => d.toISOString().split('T')[0];

      const res = await stockData(formatDate(oneMonthAgo), formatDate(today));
      if (res?.stocks) {
        setStockInfo(res.stocks);
      }
    };

    fetchDeviceListAndDefaultStock();
  }, []);



  useEffect(() => {
    const fetchInitialData = async () => {
      // Fetch devices
      const deviceData = await getAllDevices();
      if (deviceData?.devices && typeof deviceData.devices === 'object') {
        setDeviceMap(deviceData.devices);
        setDeviceList(Object.keys(deviceData.devices));
      }

      // Fetch stock info
      const today = new Date();
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(today.getMonth() - 1);
      const formatDate = (d) => d.toISOString().split('T')[0];

      const stockRes = await stockData(formatDate(oneMonthAgo), formatDate(today));
      if (stockRes?.stocks) setStockInfo(stockRes.stocks);

      // Fetch request report
      const reportRes = await getRequestReport();
      if (reportRes) {
        setRequestReport({
          purchase_request_pending: reportRes.purchase_request_pending || 0,
          purchase_order_pending: reportRes.purchase_order_pending || 0,
          payment_order_pending: reportRes.payment_order_pending || 0
        });
      }
      const stockDetailsRes = await getStockDetails();
        if (stockDetailsRes) setStockDetails(stockDetailsRes);
    };
    

    fetchInitialData();
  }, []);




  const handleChange = async (event) => {
    const selectedDevice = event.target.value;
    setSelectedOption(selectedDevice);
    const productIds = deviceMap[selectedDevice] || [];
    // setSelectedProductIds(productIds);

    if (productIds.length > 0) {
      setLoadingStock(true);
      const today = new Date();
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(today.getMonth() - 1);
      const formatDate = (d) => d.toISOString().split('T')[0];

      const res = await stockData(formatDate(oneMonthAgo), formatDate(today), productIds);
      const stockDetailsRes = await getStockDetails(productIds);

      if (res?.stocks) {
        setStockInfo(res.stocks);
      } else {
        setStockInfo(null);
      }
      if (stockDetailsRes) {
          setStockDetails(stockDetailsRes);
        }

      setLoadingStock(false);
    }
  };

  
  return (
    <Grid container spacing={2}>

      {/* Daily Stock Monitoring Card */}
      <Grid item xs={12} md={4}>
        {isLoading ? (
          <SkeletonEarningCard />
        ) : (
          <MainCard
            border={false}
            content={false}
            sx={{
              height: '100%',
              minHeight: '190px',
              bgcolor: '#592375',
              color: '#fff',
              overflow: 'hidden',
              position: 'relative'
            }}
          >
            <Box sx={{ p: 1.75 }}>
              <Grid container direction="column" spacing={2}>
                {/* Top Row */}
                <Grid item>
                  <Grid container alignItems="center" spacing={2} flexWrap="wrap">
                    <Grid item>
                      <Avatar
                        variant="rounded"
                        sx={{
                          ...theme.typography.commonAvatar,
                          ...theme.typography.largeAvatar,
                          bgcolor: '#4c1f65'
                        }}
                      >
                        <img src={EarningIcon} alt="Notification" />
                      </Avatar>
                    </Grid>

                    <Grid item>
                      <Typography
                        sx={{
                          fontSize: '1.1rem',
                          fontWeight: 500,
                          color: '#ffffff'
                        }}
                      >
                        Stock Count
                      </Typography>
                    </Grid>

                    <Grid item xs zeroMinWidth>
                      <FormControl fullWidth size="small">
                        <Select
                          labelId="device-select-label"
                          id="device-select"
                          value={selectedOption}
                          onChange={handleChange}
                          displayEmpty
                          input={
                            <OutlinedInput
                              notched={false}
                              sx={{
                                borderRadius: '12px',
                                fontWeight: 600,
                                fontSize: '0.9rem',
                                backgroundColor: '#f9f9f9',
                                boxShadow: '0px 2px 6px rgba(0,0,0,0.04)',
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'rgba(0, 0, 0, 0.12)'
                                },
                                '&:hover .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#592375'
                                },
                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                  borderColor: '#592375',
                                  borderWidth: '1.5px'
                                }
                              }}
                            />
                          }
                          sx={{
                            '& .MuiSelect-select': {
                              paddingY: 1.2,
                              paddingX: 2,
                              color: '#000'
                            },
                            '& .MuiSelect-icon': {
                              color: '#592375'
                            }
                          }}
                        >
                          <MenuItem disabled value="">
                            --Device--
                          </MenuItem>
                          {Array.isArray(deviceList) &&
                            deviceList.map((device) => (
                              <MenuItem key={device} value={device}>
                                {device}
                              </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                       {/* Show loading indicator below dropdown */}
                        {loadingStock && (
                          <Typography sx={{ color: '#fff', fontSize: '0.75rem', mt: 0.5 }}>
                            Loading stock data...
                          </Typography>
                        )}

                        {/* Product ID Display */}
                        {/* {selectedProductIds.length > 0 && (
                          <Typography sx={{ color: '#fff', fontSize: '0.7rem', mt: 0.25 }}>
                            Product IDs: {selectedProductIds.join(', ')}
                          </Typography>
                        )} */}
                    </Grid>
                  </Grid>
                </Grid>

                {/* Bottom Content */}
                <Grid item sx={{ mt: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    {/* Total Stock */}
                    <Grid item xs={12} sm="auto">
                      <Box>
                        <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
                          Total Stock
                        </Typography>
                        <Typography sx={{ fontSize: '2rem', lineHeight: 1.2 }}>
                          {totalStock}
                        </Typography>

                      </Box>
                    </Grid>

                    {/* Vertical Line */}
                    <Grid
                      item
                      sx={{
                        display: { xs: 'none', sm: 'flex' },
                        alignItems: 'center',
                        height: '80px'
                      }}
                    >
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{
                          borderColor: '#ffffff',
                          borderRightWidth: '2px',
                          opacity: 3
                        }}
                      />
                    </Grid>

                    {/* Stock In & Out */}
                    <Grid item xs>
                      <Grid container direction="column" spacing={1}>
                        {/* Stock In */}
                        <Grid item>
                          <Grid
                            container
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
                              Stock In
                            </Typography>



                            <Fade in timeout={500}>
                              <Typography
                                sx={{
                                  fontSize: '1.6rem',
                                  fontWeight: 600,
                                  fontFamily: 'Poppins, Roboto, sans-serif',
                                  color: '#ffffff', // ✅ professional deep indigo
                                  textShadow: '0px 1px 2px rgba(0,0,0,0.1)'
                                }}
                              >
                                {stockIn}
                              </Typography>
                            </Fade>



                          </Grid>
                        </Grid>

                        {/* Divider */}
                        <Grid item>
                          <Box
                            sx={{
                              height: '2px',
                              width: '100%',
                              backgroundColor: '#ffffff',
                              borderRadius: '1px'
                            }}
                          />
                        </Grid>

                        {/* Stock Out */}
                        <Grid item>
                          <Grid
                            container
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography sx={{ fontSize: '1rem', fontWeight: 500 }}>
                              Stock Out
                            </Typography>



                            <Fade in timeout={500}>
                              <Typography sx={{ fontSize: '1.6rem', fontWeight: 600, color: '#ffffff' }}>
                                {stockOut}
                              </Typography>
                            </Fade>



                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </MainCard>
        )}
      </Grid>

      {/* Three Horizontal Cards: Purchase Request Pending | Purchase Order Pending | payment order Pending */}
        <Grid item xs={12} sm={6} md={4}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[
              { label: 'Purchase Request Pending', count: requestReport.purchase_request_pending },
              { label: 'Purchase Order Pending', count: requestReport.purchase_order_pending },
              { label: 'Payment Order Pending', count: requestReport.payment_order_pending }
            ].map((item) => (
              <Box
                key={item.label}
                sx={{
                  border: '1px solid #592375',
                  borderRadius: '8px',
                  backgroundColor: '#ffffff',
                  overflow: 'hidden',
                  minHeight: '50px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <MainCard
                  border={false}
                  content={false}
                  sx={{
                    bgcolor: 'transparent',
                    color: '#000000',
                    width: '100%',
                    boxShadow: 'none'
                  }}
                >
                  <Box sx={{ px: 2, py: 1 }}>
                    <Grid
                      container
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Grid item xs>
                        <Typography
                          sx={{
                            fontSize: { xs: '1rem', sm: '1.05rem' },
                            fontWeight: 400,
                            color: '#000000',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {item.label}
                        </Typography>
                      </Grid>
                      <Grid item>
                        <Typography
                          sx={{
                            fontSize: { xs: '1rem', sm: '1.05rem' },
                            fontWeight: 600,
                            color: '#592375'
                          }}
                        >
                          {item.count}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </MainCard>
              </Box>
            ))}
          </Box>
        </Grid>

      {/* Requests Pending Card */}
      <Grid item xs={12} sm={6} md={4}>
        {isLoading ? (
          <SkeletonEarningCard />
        ) : (
          <CardWrapper
            border={true}
            content={false}
            sx={{
              '&::before': { display: 'none' },
              '&::after': { display: 'none' },
              backgroundColor: '#ffffff',
              position: 'relative',
              overflow: 'hidden',
              border: '1px solid #592375',
              borderRadius: '12px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              minHeight: '190px'
            }}
          >
            {/* Top section with purple background */}
            <Box
              sx={{
                backgroundColor: '#592375',
                px: 2,
                py: 1.5
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.125rem' },
                  fontWeight: 500,
                  color: '#ffffff'
                }}
              >
                Stock Details
              </Typography>
            </Box>

            {/* White separator line */}
            <Box
              sx={{
                height: '3px',
                width: '100%',
                backgroundColor: '#ffffff'
              }}
            />

            {/* Bottom section */}
            <Box
              sx={{
                backgroundColor: '#ffffff',
                px: 2,
                py: 1.5,
                flexGrow: 1
              }}
            >
              <Grid container direction="column" spacing={1}>
                {/* Status Item Component */}
                {[
                  { label: 'To be shipped', value: stockDetails?.to_be_shipped ?? 0  },
                  { label: 'Out for delivery', value: stockDetails?.out_of_delivery ?? 0  },
                  { label: 'Delivered', value: stockDetails?.orders_delivered ?? 0  }
                ].map((item, index) => (
                  <React.Fragment key={item.label}>
                    <Grid item>
                      <Grid container justifyContent="space-between" alignItems="center">
                        <Typography
                          sx={{
                            fontSize: { xs: '0.95rem', sm: '1rem' },
                            fontWeight: 500,
                            color: '#000000'
                          }}
                        >
                          {item.label}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: { xs: '0.95rem', sm: '1rem' },
                            fontWeight: 500,
                            color: '#592375'
                          }}
                        >
                          {item.value}
                        </Typography>
                      </Grid>
                    </Grid>
                    {index < 2 && (
                      <Grid item>
                        <Box
                          sx={{
                            height: '1px',
                            width: '100%',
                            backgroundColor: '#7F7F7F' ,
                            mt: 0.5
                          }}
                        />
                      </Grid>
                    )}
                  </React.Fragment>
                ))}
              </Grid>
            </Box>
          </CardWrapper>
        )}
      </Grid>
      {/* Create Vendor Order Card */}
      {/* <Grid item xs={12} sx={{ mt: 0 }}>
        <Box display="flex" justifyContent="flex-end" sx={{ mt: -1 }}>
          <Button
            variant="contained"
            sx={{
              backgroundColor: '#688B2A',
              color: '#fff',
              textTransform: 'none',
              borderRadius: '5px',
              px: 2.5,
              py: 1,
              fontSize: '0.9rem',
              fontWeight: 500,
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#688B2A'
              }
            }}
            onClick={() => {
              console.log('Vendor Order button clicked');
            }}
          >
            Create Vendor Order
          </Button>
        </Box>
      </Grid> */}

      {/*This renders the table based on selectedOption */}
      <Grid item xs={12}>
        <ApplicationSalesCard selectedOption={selectedOption} />
      </Grid>
    </Grid>
  );
};

EarningCard.propTypes = {
  isLoading: PropTypes.bool,
  stockInfo: PropTypes.object
};

export default EarningCard;

